import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

const MobilePatientForm = ({
  patientInfo = {},
  notes = '',
  onSubmit,
  onBack,
  loading = false,
  bloodTestRequired = false,
  bloodTestPrice = '0'
}) => {
  const [formData, setFormData] = useState({
    first_name: patientInfo.first_name || '',
    last_name: patientInfo.last_name || '',
    email: patientInfo.email || '',
    phone: patientInfo.phone || '',
    notes: notes || ''
  });

  const [errors, setErrors] = useState({});
  const [showNotes, setShowNotes] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (formData.phone.trim() && !/^[0-9+\s()-]{8,20}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const patientData = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim()
    };

    onSubmit(patientData, formData.notes);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="600">
          Your Details
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your contact information for the appointment
      </Typography>

      {/* Blood test notice */}
      {bloodTestRequired && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            A blood test is required for this appointment.
            {bloodTestPrice && bloodTestPrice !== '0' && (
              <strong> Cost: ${bloodTestPrice}</strong>
            )}
          </Typography>
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Name row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              name="first_name"
              label="First Name"
              value={formData.first_name}
              onChange={handleChange}
              error={!!errors.first_name}
              helperText={errors.first_name}
              disabled={loading}
              fullWidth
              required
              autoComplete="given-name"
              inputProps={{
                style: { fontSize: 16 } // Prevents iOS zoom
              }}
              sx={{
                '& .MuiInputBase-root': { height: 56 }
              }}
            />

            <TextField
              name="last_name"
              label="Last Name"
              value={formData.last_name}
              onChange={handleChange}
              error={!!errors.last_name}
              helperText={errors.last_name}
              disabled={loading}
              fullWidth
              required
              autoComplete="family-name"
              inputProps={{
                style: { fontSize: 16 }
              }}
              sx={{
                '& .MuiInputBase-root': { height: 56 }
              }}
            />
          </Box>

          {/* Email */}
          <TextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={loading}
            fullWidth
            required
            autoComplete="email"
            inputProps={{
              style: { fontSize: 16 }
            }}
            sx={{
              mb: 2,
              '& .MuiInputBase-root': { height: 56 }
            }}
          />

          {/* Phone */}
          <TextField
            name="phone"
            label="Phone (optional)"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone || 'For SMS appointment reminders'}
            disabled={loading}
            fullWidth
            autoComplete="tel"
            inputProps={{
              style: { fontSize: 16 }
            }}
            sx={{
              mb: 2,
              '& .MuiInputBase-root': { height: 56 }
            }}
          />

          {/* Notes toggle */}
          <Box
            onClick={() => setShowNotes(!showNotes)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <Typography variant="body2">
              Add notes for your practitioner
            </Typography>
            {showNotes ? <ExpandLess /> : <ExpandMore />}
          </Box>

          <Collapse in={showNotes}>
            <TextField
              name="notes"
              label="Notes (optional)"
              value={formData.notes}
              onChange={handleChange}
              disabled={loading}
              fullWidth
              multiline
              rows={3}
              placeholder="Any information you'd like to share..."
              inputProps={{
                style: { fontSize: 16 }
              }}
              sx={{ mb: 2 }}
            />
          </Collapse>

          {/* Error summary */}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the errors above
            </Alert>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 2,
              height: 56,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue to Payment'
            )}
          </Button>
        </form>
      </Paper>

      {/* Privacy note */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 2 }}
      >
        Your information is secure and will only be used for this appointment.
      </Typography>
    </Box>
  );
};

export default MobilePatientForm;
